import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './TelegramMessaging.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const WEEKDAYS = [
  { v: 0, l: 'Sunday' },
  { v: 1, l: 'Monday' },
  { v: 2, l: 'Tuesday' },
  { v: 3, l: 'Wednesday' },
  { v: 4, l: 'Thursday' },
  { v: 5, l: 'Friday' },
  { v: 6, l: 'Saturday' },
];

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago' });
  } catch {
    return iso;
  }
}

function fmtCentralFromDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return '';
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [hh, mm] = timeStr.split(':').map(Number);
  if (![y, mo, d, hh, mm].every((n) => Number.isFinite(n))) return '';
  try {
    return new Date(y, mo - 1, d, hh, mm, 0).toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return `${dateStr} ${timeStr}`;
  }
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v6l4 2" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function PickerField({ label, type, value, onChange, required }) {
  const ref = useRef(null);
  const openPicker = () => {
    const el = ref.current;
    if (el?.showPicker) el.showPicker();
    else el?.focus();
  };
  return (
    <label>
      {label}
      <div className="tg-picker">
        <input ref={ref} type={type} value={value} onChange={onChange} required={required} />
        <button type="button" className="tg-picker-btn" onClick={openPicker} aria-label={`Open ${type}`}>
          {type === 'date' ? <CalendarIcon /> : <ClockIcon />}
        </button>
      </div>
    </label>
  );
}

export default function TelegramMessaging({ isAdmin = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [botToken, setBotToken] = useState('');
  const [contactId, setContactId] = useState('');
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('now');
  const [runAt, setRunAt] = useState('');
  const [runDate, setRunDate] = useState('');
  const [runTime, setRunTime] = useState('');
  const [timeSetNote, setTimeSetNote] = useState('');
  const [recurrence, setRecurrence] = useState('daily');
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [weekday, setWeekday] = useState(1);
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [manualChatId, setManualChatId] = useState('');
  const [manualName, setManualName] = useState('');
  const [chats, setChats] = useState([]);
  const [openChatId, setOpenChatId] = useState('');
  const [thread, setThread] = useState([]);
  const [reply, setReply] = useState('');

  const loadInbox = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_BASE}/telegram/inbox`, { headers: authHeaders() });
      setChats(d.chats || []);
    } catch {
      /* status load still works */
    }
  }, []);

  const loadThread = useCallback(async (chatId) => {
    if (!chatId) {
      setThread([]);
      return;
    }
    try {
      const { data: d } = await axios.get(
        `${API_BASE}/telegram/inbox/${encodeURIComponent(chatId)}`,
        { headers: authHeaders() }
      );
      setThread(d.messages || []);
    } catch {
      /* keep previous thread */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_BASE}/telegram/status`, { headers: authHeaders() });
      setData(d);
      setContactId((prev) => prev || (d.contacts?.[0] ? String(d.contacts[0].id) : ''));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadInbox();
  }, [load, loadInbox]);

  useEffect(() => {
    const t = setInterval(() => {
      loadInbox();
      if (openChatId) loadThread(openChatId);
    }, 8000);
    return () => clearInterval(t);
  }, [loadInbox, loadThread, openChatId]);

  const flash = (msg, isErr) => {
    setError(isErr ? msg : '');
    setOk(isErr ? '' : msg);
  };

  const openChat = async (chatId) => {
    setOpenChatId(chatId);
    await loadThread(chatId);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!openChatId || !reply.trim()) return;
    setBusy(true);
    flash('', false);
    try {
      await axios.post(
        `${API_BASE}/telegram/send`,
        { chatId: openChatId, message: reply.trim() },
        { headers: authHeaders() }
      );
      setReply('');
      await loadThread(openChatId);
      await loadInbox();
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    flash('', false);
    try {
      await axios.post(`${API_BASE}/telegram/connect`, { botToken }, { headers: authHeaders() });
      setBotToken('');
      flash('Bot connected.');
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect this Telegram bot from Omni?')) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/telegram/disconnect`, {}, { headers: authHeaders() });
      flash('Bot disconnected.');
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const syncContacts = async () => {
    setBusy(true);
    flash('', false);
    try {
      const { data: d } = await axios.post(`${API_BASE}/telegram/contacts/sync`, {}, { headers: authHeaders() });
      flash(`Synced. ${d.contacts?.length || 0} contact(s).`);
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE}/telegram/contacts`,
        { chatId: manualChatId, displayName: manualName },
        { headers: authHeaders() }
      );
      setManualChatId('');
      setManualName('');
      flash('Contact saved.');
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const sendOrSchedule = async (e) => {
    e.preventDefault();
    setBusy(true);
    flash('', false);
    try {
      if (mode === 'now' || !(data?.canManageBot ?? isAdmin)) {
        await axios.post(
          `${API_BASE}/telegram/send`,
          { contactId: Number(contactId), message },
          { headers: authHeaders() }
        );
        flash('Message sent.');
      } else if (mode === 'once') {
        if (!runAt) {
          flash('Click Set time before scheduling.', true);
          setBusy(false);
          return;
        }
        await axios.post(
          `${API_BASE}/telegram/jobs`,
          { contactId: Number(contactId), message, kind: 'once', runAt: new Date(runAt).toISOString() },
          { headers: authHeaders() }
        );
        flash('One-time message scheduled.');
      } else {
        await axios.post(
          `${API_BASE}/telegram/jobs`,
          {
            contactId: Number(contactId),
            message,
            kind: 'recurring',
            recurrence,
            hour: Number(hour),
            minute: Number(minute),
            weekday: Number(weekday),
            intervalMinutes: Number(intervalMinutes),
            timezone: 'America/Chicago',
          },
          { headers: authHeaders() }
        );
        flash('Recurring message scheduled.');
      }
      setMessage('');
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  const toggleJob = async (id) => {
    try {
      await axios.post(`${API_BASE}/telegram/jobs/${id}/toggle`, {}, { headers: authHeaders() });
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Delete this scheduled message?')) return;
    try {
      await axios.delete(`${API_BASE}/telegram/jobs/${id}`, { headers: authHeaders() });
      await load();
    } catch (err) {
      flash(err.response?.data?.error || err.message, true);
    }
  };

  const connected = !!data?.account?.connected;
  const canManage = data?.canManageBot ?? isAdmin;
  const contacts = data?.contacts || [];
  const jobs = data?.jobs || [];
  const recent = data?.recent || [];
  const memberConnected = !!data?.memberConnected;
  const botHandle = data?.account?.botUsername ? `@${data.account.botUsername}` : '@omni_traders_bot';

  return (
    <div className="tg-page">
      <h1>Telegram</h1>
      <p className="tg-lead">
        {canManage
          ? 'Send a message now, schedule it once, or set it to repeat. Times use Central Time.'
          : 'Inbox and contacts are everyone connected to the Omni Traders bot. You can send a message now; scheduling is for admins.'}
      </p>

      {error && <p className="tg-banner tg-err">{error}</p>}
      {ok && <p className="tg-banner tg-ok">{ok}</p>}
      {loading && !data && <p className="tg-muted">Loading…</p>}

      {canManage && (
      <section className="tg-card">
        <h2>1. Connect a bot</h2>
        <p className="tg-hint">
          Telegram does not let Omni read your personal phone contacts. Create a bot with{' '}
          <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">
            @BotFather
          </a>
          , paste the token, then have each person tap <strong>Start</strong> on your bot (or add the bot to a
          group). After that they show up as contacts.
        </p>
        {connected ? (
          <div className="tg-connected">
            <p>
              Connected as{' '}
              <strong>{data.account.botUsername ? `@${data.account.botUsername}` : 'bot'}</strong>
              {data.account.botLink && (
                <>
                  {' '}
                  —{' '}
                  <a href={data.account.botLink} target="_blank" rel="noreferrer">
                    {data.account.botLink}
                  </a>
                </>
              )}
            </p>
            <button type="button" className="tg-btn ghost" disabled={busy} onClick={disconnect}>
              Disconnect
            </button>
          </div>
        ) : (
          <form className="tg-row" onSubmit={connect}>
            <input
              type="password"
              placeholder="Bot token from @BotFather"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" className="tg-btn" disabled={busy || !botToken.trim()}>
              Connect
            </button>
          </form>
        )}
      </section>
      )}

      {!canManage && memberConnected && (
        <section className="tg-card tg-status">
          <p>
            Connected to <strong>{botHandle}</strong>
          </p>
        </section>
      )}

      <section className="tg-card">
        <h2>Inbox</h2>
        <p className="tg-hint">
          This is the bot chat only — messages people send to your bot, and your replies. It is not your full
          personal Telegram. New messages from before this update will not appear; send a new note in the bot
          chat to start the thread here. Refreshes every few seconds.
        </p>
        <div className="tg-inbox">
          <div className="tg-inbox-list">
            {chats.length === 0 ? (
              <p className="tg-muted">No chats yet.</p>
            ) : (
              chats.map((c) => (
                <button
                  key={c.id || c.chatId}
                  type="button"
                  className={`tg-chat-item${openChatId === c.chatId ? ' active' : ''}`}
                  onClick={() => openChat(c.chatId)}
                >
                  <strong>{c.displayName}</strong>
                  <span>{c.lastBody || 'No messages yet'}</span>
                </button>
              ))
            )}
          </div>
          <div className="tg-inbox-thread">
            {!openChatId ? (
              <p className="tg-muted">Select a chat.</p>
            ) : (
              <>
                <div className="tg-thread-msgs">
                  {thread.length === 0 ? (
                    <p className="tg-muted">No messages stored yet. Ask them to send something in Telegram.</p>
                  ) : (
                    thread.map((m) => (
                      <div key={m.id} className={`tg-bubble ${m.direction === 'out' ? 'out' : 'in'}`}>
                        <div className="tg-bubble-meta">
                          {m.direction === 'out' ? 'You' : m.from_name || 'Them'} · {fmtWhen(m.sent_at)}
                        </div>
                        <div className="tg-bubble-body">{m.body}</div>
                      </div>
                    ))
                  )}
                </div>
                <form className="tg-reply" onSubmit={sendReply}>
                  <input
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Reply…"
                    disabled={!connected || busy}
                  />
                  <button type="submit" className="tg-btn" disabled={!connected || busy || !reply.trim()}>
                    Send
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="tg-card">
        <h2>2. Contacts</h2>
        <div className="tg-row tg-actions">
          <button type="button" className="tg-btn" disabled={busy || !connected} onClick={syncContacts}>
            Refresh from Telegram
          </button>
        </div>
        {contacts.length === 0 ? (
          <p className="tg-muted">No contacts yet. Open the bot link, tap Start, then click Refresh.</p>
        ) : (
          <ul className="tg-list">
            {contacts.map((c) => (
              <li key={c.id}>
                <strong>{c.displayName}</strong>
                <span className="tg-muted">
                  {c.isGroup ? 'group' : 'person'} · {c.chatId}
                </span>
              </li>
            ))}
          </ul>
        )}
        {canManage && (
        <form className="tg-row tg-manual" onSubmit={addManual}>
          <input
            placeholder="Chat ID (numbers)"
            value={manualChatId}
            onChange={(e) => setManualChatId(e.target.value)}
          />
          <input
            placeholder="Label (optional)"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
          />
          <button type="submit" className="tg-btn ghost" disabled={busy || !manualChatId.trim()}>
            Add by ID
          </button>
        </form>
        )}
      </section>

      <section className="tg-card">
        <h2>{canManage ? '3. Send or schedule' : '3. Send'}</h2>
        <form onSubmit={sendOrSchedule}>
          <label>
            Contact
            <select value={contactId} onChange={(e) => setContactId(e.target.value)} required>
              <option value="">Select…</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>

          {canManage && (
          <>
          <div className="tg-modes">
            {[
              ['now', 'Now'],
              ['once', 'Later (one time)'],
              ['recurring', 'Repeat'],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                className={mode === v ? 'active' : ''}
                onClick={() => setMode(v)}
              >
                {l}
              </button>
            ))}
          </div>
          <p className="tg-hint">Those three buttons only pick the type. Sending happens with the blue button below.</p>

          {mode === 'once' && (
            <div className="tg-when">
              <div className="tg-row tg-when-row">
                <PickerField
                  label="Date"
                  type="date"
                  value={runDate}
                  required
                  onChange={(e) => {
                    setRunDate(e.target.value);
                    setTimeSetNote('');
                    setRunAt('');
                  }}
                />
                <PickerField
                  label="Time (Central)"
                  type="time"
                  value={runTime}
                  required
                  onChange={(e) => {
                    setRunTime(e.target.value);
                    setTimeSetNote('');
                    setRunAt('');
                  }}
                />
                <button
                  type="button"
                  className="tg-btn"
                  disabled={!runDate || !runTime}
                  onClick={() => {
                    const next = `${runDate}T${runTime}`;
                    setRunAt(next);
                    const label = fmtCentralFromDateTime(runDate, runTime);
                    setTimeSetNote(label ? `Time set: ${label} Central` : `Time set: ${runDate} ${runTime} Central`);
                    flash(label ? `Time set: ${label} Central` : `Time set: ${runDate} ${runTime} Central`);
                  }}
                >
                  Set time
                </button>
              </div>
              {timeSetNote ? <p className="tg-banner tg-ok">{timeSetNote}</p> : (
                <p className="tg-hint">Pick the date and time, then click <strong>Set time</strong>.</p>
              )}
            </div>
          )}

          {mode === 'recurring' && (
            <>
              <label>
                Repeat
                <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
                  <option value="daily">Every day</option>
                  <option value="weekly">Every week</option>
                  <option value="interval">Every N minutes</option>
                </select>
              </label>
              {recurrence === 'weekly' && (
                <label>
                  Weekday
                  <select value={weekday} onChange={(e) => setWeekday(e.target.value)}>
                    {WEEKDAYS.map((d) => (
                      <option key={d.v} value={d.v}>
                        {d.l}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {recurrence === 'interval' ? (
                <label>
                  Minutes
                  <input
                    type="number"
                    min={1}
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(e.target.value)}
                  />
                </label>
              ) : (
                <div className="tg-when">
                  <div className="tg-row tg-when-row">
                    <label>
                      Hour (0–23)
                      <input type="number" min={0} max={23} value={hour} onChange={(e) => { setHour(e.target.value); setTimeSetNote(''); }} />
                    </label>
                    <label>
                      Minute (0–59)
                      <input type="number" min={0} max={59} value={minute} onChange={(e) => { setMinute(e.target.value); setTimeSetNote(''); }} />
                    </label>
                    <button
                      type="button"
                      className="tg-btn"
                      onClick={() => {
                        const h = Math.min(23, Math.max(0, Number(hour) || 0));
                        const m = Math.min(59, Math.max(0, Number(minute) || 0));
                        setHour(h);
                        setMinute(m);
                        const label =
                          recurrence === 'weekly'
                            ? `Time set: every ${WEEKDAYS.find((d) => d.v === Number(weekday))?.l || 'week'} at ${pad2(h)}:${pad2(m)} Central`
                            : `Time set: every day at ${pad2(h)}:${pad2(m)} Central`;
                        setTimeSetNote(label);
                        flash(label);
                      }}
                    >
                      Set time
                    </button>
                  </div>
                  {timeSetNote ? <p className="tg-banner tg-ok">{timeSetNote}</p> : (
                    <p className="tg-hint">Choose the hour and minute, then click <strong>Set time</strong>.</p>
                  )}
                </div>
              )}
            </>
          )}
          </>
          )}

          <label>
            Message
            <textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              placeholder="Type the Telegram message…"
            />
          </label>
          <button type="submit" className="tg-btn tg-btn-primary" disabled={busy || !connected || !contactId || !message.trim()}>
            {busy ? 'Working…' : canManage && mode !== 'now' ? 'Schedule message' : 'Send message now'}
          </button>
        </form>
      </section>

      {canManage && (
      <section className="tg-card">
        <h2>Scheduled</h2>
        {jobs.length === 0 ? (
          <p className="tg-muted">No scheduled messages.</p>
        ) : (
          <table className="tg-table">
            <thead>
              <tr>
                <th>When</th>
                <th>To</th>
                <th>Message</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className={j.enabled ? '' : 'off'}>
                  <td>
                    {j.kind === 'once' ? 'Once' : j.recurrence}
                    <br />
                    <span className="tg-muted">{fmtWhen(j.next_run_at)}</span>
                    {j.last_error && <div className="tg-err-inline">{j.last_error}</div>}
                  </td>
                  <td>{j.chat_id}</td>
                  <td className="tg-msg">{j.message_text}</td>
                  <td className="tg-job-actions">
                    <button type="button" className="tg-btn ghost" onClick={() => toggleJob(j.id)}>
                      {j.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className="tg-btn ghost" onClick={() => deleteJob(j.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      )}

      <section className="tg-card">
        <h2>Recent sends</h2>
        {recent.length === 0 ? (
          <p className="tg-muted">None yet.</p>
        ) : (
          <ul className="tg-list">
            {recent.map((r) => (
              <li key={r.id}>
                <strong className={r.status === 'sent' ? 'ok' : 'bad'}>{r.status}</strong>
                <span>{fmtWhen(r.sent_at)}</span>
                <span className="tg-muted">{r.chat_id}</span>
                {r.error_message && <span className="tg-err-inline">{r.error_message}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
